import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Home, Phone, Mail, MapPin, Cpu, Cylinder, ExternalLink,
  Zap, Gauge, Wallet, BatteryCharging, Signal, Power, PowerOff, Radio,
  RefreshCcw, Repeat, TreeDeciduous, Flame, Wind, TrendingUp,
  Pencil, Trash2, Download, StickyNote,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import { HouseholdEditDialog } from "@/components/household/HouseholdEditDialog";
import { useStations } from "@/components/supply/shared";

const fmtUGX = (n, cur = "UGX") => `${cur} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtM3  = (n) => `${Number(n || 0).toFixed(3)} m³`;
const fmtKg  = (n) => `${Number(n || 0).toFixed(2)} kg`;

// Tabs (data-testid uses same key)
const TABS = [
  { key: "details",   label: "Details" },
  { key: "frames",    label: "Frame log" },
  { key: "insights",  label: "Meter insights" },
  { key: "analytics", label: "Analytics" },
  { key: "impact",    label: "Impact" },
];

export default function HouseholdView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [cylinder, setCylinder] = useState(null);
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/households/${id}/summary`);
      setSummary(data);
      if (data.meter?.id) {
        const [cyl, fr] = await Promise.all([
          api.get(`/meters/${data.meter.id}/cylinder/level`),
          api.get(`/meters/${data.meter.id}/frames`, { params: { limit: 100 } }),
        ]);
        setCylinder(cyl.data);
        setFrames(fr.data.data || []);
      }
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load household");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const hh = summary?.household;
  const meter = summary?.meter;
  const cfg = summary?.config || {};

  if (loading && !summary) {
    return <div className="p-8 text-sm text-slate-400" data-testid="household-view-loading">Loading household…</div>;
  }
  if (!hh) return null;

  const isActive = Boolean(meter) && (hh.state || "ACTIVE") === "ACTIVE";

  return (
    <div className="space-y-6" data-testid="household-view-page">
      {/* Top bar */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => nav("/households")}
            className="text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1"
            data-testid="household-view-back"
          >
            <ArrowLeft size={14} /> Households
          </button>
          <div className="mt-2 flex items-baseline gap-3">
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{hh.household_name}</h1>
            <span
              className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${
                isActive
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
              data-testid="household-view-status"
            >
              {hh.state && hh.state !== "ACTIVE" ? hh.state.charAt(0) + hh.state.slice(1).toLowerCase() : meter ? "Active" : "Dormant"}
            </span>
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {meter ? <>Meter <span className="font-mono text-slate-700">{meter.serial_number}</span></> : "No meter assigned"}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full h-auto flex-wrap gap-1 rounded-sm bg-slate-100 p-1 justify-start" data-testid="household-tabs-list">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`household-tab-${t.key}`}
              className="rounded-sm px-3 py-1.5 text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="details" className="mt-4"><DetailsTab hh={hh} meter={meter} reload={load} summaryType={summary?.cylinder_type} /></TabsContent>
        <TabsContent value="frames" className="mt-4"><FramesTab meter={meter} frames={frames} /></TabsContent>
        <TabsContent value="insights" className="mt-4"><InsightsTab meter={meter} cylinder={cylinder} cfg={cfg} /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab summary={summary} /></TabsContent>
        <TabsContent value="impact" className="mt-4"><ImpactTab summary={summary} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------- Tabs -------------------------------------------------
function DetailRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="h-7 w-7 rounded-sm bg-slate-100 grid place-items-center text-slate-500">
        <Icon size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className={`text-sm text-slate-900 ${mono ? "font-mono tabular-nums" : ""}`}>{value || <span className="text-slate-400 italic">—</span>}</div>
      </div>
    </div>
  );
}

function DetailsTab({ hh, meter, reload, summaryType }) {
  const nav = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [stations] = useStations();
  const remove = async () => {
    if (!window.confirm("Delete this household? Meters remain but will be unassigned.")) return;
    try { await api.delete(`/households/${hh.id}`); toast.success("Household deleted"); nav("/households"); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Delete failed"); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="household-details-tab">
      <div className="md:col-span-2 bg-white border border-slate-200 rounded-sm p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <DetailRow icon={Home}  label="Household name" value={hh.household_name} />
          <DetailRow icon={Phone} label="Phone"          value={hh.phone ? `+${hh.phone}` : ""} mono />
          <DetailRow icon={Mail}  label="Email"          value={hh.email} />
          <DetailRow icon={MapPin} label="Address"       value={hh.address} />
          <DetailRow icon={Cylinder} label="Cylinder type" value={summaryType ? `${summaryType.name} · UGX ${Number(summaryType.price_per_m3).toLocaleString(undefined, { maximumFractionDigits: 0 })}/m³` : ""} />
          <DetailRow icon={MapPin} label="Stabex station" value={stations.find((s) => s.id === hh.station_id)?.name || (hh.lat != null ? "Not assigned" : "Not assigned · no GPS")} />
          <DetailRow icon={StickyNote} label="Notes"     value={hh.notes} />
          <DetailRow icon={Power} label="Status"         value={<StatusPill value={hh.state || "ACTIVE"} />} />
          <DetailRow icon={Cpu}   label="Meter number"   value={meter?.serial_number} mono />
          <DetailRow icon={Radio} label="IMEI"           value={meter?.imei} mono />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => setEditOpen(true)}
            data-testid="household-details-edit"
          >
            <Pencil size={13} className="mr-1" /> Edit
          </Button>
          <Button
            variant="outline"
            className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
            onClick={remove}
            data-testid="household-details-delete"
          >
            <Trash2 size={13} className="mr-1" /> Delete
          </Button>
          {meter && (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => nav(`/meters/${meter.id}`)}
              data-testid="household-details-open-meter"
            >
              <ExternalLink size={13} className="mr-1" /> Open meter page
            </Button>
          )}
        </div>
      </div>

      {/* QR sticker card */}
      <HouseholdQrCard hh={hh} />
      <HouseholdEditDialog hh={hh} open={editOpen} onOpenChange={setEditOpen} onSaved={reload} />
    </div>
  );
}

function HouseholdQrCard({ hh }) {
  const publicUrl = `${window.location.origin}/h/${hh.id}`;
  const downloadPng = () => {
    const svg = document.getElementById("household-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 800;
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      const link = document.createElement("a");
      link.download = `gasify-${hh.household_name.replace(/\s+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = url;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="household-qr-card">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">QR code</div>
      <div className="border border-slate-200 rounded-sm p-4 bg-white grid place-items-center">
        <QRCodeSVG
          id="household-qr-svg"
          value={publicUrl}
          size={180}
          bgColor="#ffffff"
          fgColor="#0f172a"
          level="M"
          data-testid="household-qr-svg"
        />
      </div>
      <Button size="sm" variant="outline" onClick={downloadPng} className="rounded-sm h-8 mt-3 w-full" data-testid="qr-download-png">
        <Download size={13} className="mr-1" /> Download PNG
      </Button>
    </div>
  );
}

function FramesTab({ meter, frames }) {
  const [viewFrame, setViewFrame] = useState(null);
  if (!meter) return <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-sm text-slate-400">No meter linked.</div>;

  const fmtVal = (k, v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
      if (k.endsWith("_m3") || k.endsWith("_kg") || k === "pressure_bar" || k === "battery_voltage") return v.toFixed(3);
      return String(v);
    }
    if (typeof v === "boolean") return v ? "yes" : "no";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  // Pick the top three most-useful decoded fields to show inline
  const summariseDecoded = (d) => {
    if (!d || Object.keys(d).length === 0) return "—";
    const order = ["accflow_m3", "reading_m3", "total_purchase_m3", "remaining_flow_m3",
                   "valve_state", "battery_voltage", "pressure_bar", "temperature_c",
                   "amount", "gas_m3"];
    const rows = order.filter((k) => k in d).slice(0, 3);
    if (!rows.length) {
      // Fallback: show first 3 keys of the dict
      return Object.entries(d).slice(0, 3).map(([k, v]) => `${k}=${fmtVal(k, v)}`).join(" · ");
    }
    return rows.map((k) => `${k.replace(/_m3$/, " m³").replace(/_/g, " ")}=${fmtVal(k, d[k])}`).join(" · ");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="household-frames-tab">
      <div className="max-h-[540px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Time</th>
              <th className="text-left px-4 py-2 font-semibold">Dir</th>
              <th className="text-left px-4 py-2 font-semibold">Cmd / Type</th>
              <th className="text-left px-4 py-2 font-semibold">Decoded</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {frames.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">No frames yet.</td></tr>
            )}
            {frames.map((f, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`frame-row-${i}`}>
                <td className="px-4 py-2 font-mono text-slate-500 whitespace-nowrap">{f.ts ? new Date(f.ts).toLocaleString() : "—"}</td>
                <td className="px-4 py-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${f.direction === "UPLINK" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{f.direction}</span>
                </td>
                <td className="px-4 py-2 text-slate-700 font-mono">{f.cmd || "—"}</td>
                <td className="px-4 py-2 text-slate-700 font-mono truncate max-w-[380px]" title={summariseDecoded(f.decoded)}>{summariseDecoded(f.decoded)}</td>
                <td className="px-4 py-2 text-slate-500">{f.status || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant="outline" size="sm" className="rounded-sm h-7 px-2"
                    onClick={() => setViewFrame(f)}
                    data-testid={`frame-view-${i}`}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <FrameDetailDialog frame={viewFrame} onClose={() => setViewFrame(null)} fmtVal={fmtVal} />
    </div>
  );
}

function FrameDetailDialog({ frame, onClose, fmtVal }) {
  if (!frame) return null;
  const decoded = frame.decoded || {};
  return (
    <FrameDialogShell onClose={onClose}>
      <div className="p-5 space-y-4" data-testid="frame-detail-dialog">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Frame detail</div>
            <div className="font-heading text-lg font-bold text-slate-900">{frame.cmd || "—"}</div>
          </div>
          <span className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${frame.direction === "UPLINK" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{frame.direction}</span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Time</span><span className="font-mono text-slate-800">{frame.ts ? new Date(frame.ts).toLocaleString() : "—"}</span></div>
          <div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Bytes</span><span className="font-mono text-slate-800 tabular-nums">{frame.bytes ?? "—"}</span></div>
          {frame.source && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Source</span><span className="font-mono text-slate-800">{frame.source}</span></div>)}
          {frame.bridge_id && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Bridge</span><span className="font-mono text-slate-800">{frame.bridge_id}</span></div>)}
          {frame.status && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Status</span><span className="text-slate-800">{frame.status}</span></div>)}
          {frame.fid && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">FID</span><span className="font-mono text-slate-800">{frame.fid}</span></div>)}
          {frame.sent_at && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">Sent</span><span className="font-mono text-slate-800">{new Date(frame.sent_at).toLocaleString()}</span></div>)}
          {frame.ack_at && (<div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold block">ACK</span><span className="font-mono text-slate-800">{new Date(frame.ack_at).toLocaleString()}</span></div>)}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Decoded fields</div>
          {Object.keys(decoded).length === 0 ? (
            <div className="text-xs text-slate-400 italic">No decoded fields for this frame.</div>
          ) : (
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(decoded).map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-100 first:border-t-0">
                      <td className="px-3 py-1.5 text-slate-500 font-mono text-[11px] w-1/3 uppercase tracking-widest">{k}</td>
                      <td className="px-3 py-1.5 text-slate-900 font-mono tabular-nums">{fmtVal(k, v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <details className="text-xs">
          <summary className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold cursor-pointer">Raw hex (advanced)</summary>
          <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm font-mono break-all text-slate-700">{frame.hex || "—"}</div>
        </details>
      </div>
    </FrameDialogShell>
  );
}

function FrameDialogShell({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4"
      onClick={onClose}
      role="dialog"
      data-testid="frame-dialog-shell"
    >
      <div
        className="bg-white rounded-sm border border-slate-200 shadow-xl w-full max-w-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function InsightsStat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="h-9 w-9 rounded-sm bg-slate-100 grid place-items-center text-slate-500"><Icon size={16} strokeWidth={1.9} /></div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-heading text-lg font-bold tabular-nums text-slate-900 leading-none mt-1">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-1 tabular-nums">{sub}</div>}
      </div>
    </div>
  );
}

function InsightsTab({ meter, cylinder, cfg }) {
  if (!meter) return <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-sm text-slate-400">No meter linked.</div>;
  const currency = cfg?.currency || "UGX";
  const price = Number(cfg?.gas_price_per_m3 || 0);
  const pct = cylinder ? Math.max(0, Math.min(100, Number(cylinder.remaining_pct || 0))) : 0;
  const tone = cylinder?.is_low ? "bg-flame-500" : pct < 50 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="space-y-4" data-testid="household-insights-tab">
      {/* Cylinder level card */}
      {cylinder && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-sm bg-brand-50 text-brand-700 border border-brand-100 grid place-items-center"><Cylinder size={16} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cylinder level</div>
                <div className="font-heading text-base font-bold text-slate-900">{cylinder.cylinder_kg} kg cylinder</div>
              </div>
            </div>
            <div className="font-heading text-2xl font-bold tabular-nums">{pct.toFixed(1)}%</div>
          </div>
          <div className="h-3 w-full rounded-sm bg-slate-100 overflow-hidden mt-3">
            <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 tabular-nums">
            <div>{Number(cylinder.remaining_kg || 0).toFixed(2)} kg · {Number(cylinder.remaining_m3 || 0).toFixed(3)} m³ remaining</div>
            <div>{cylinder.days_to_empty != null ? `${cylinder.days_to_empty} days to empty` : "no consumption yet"}</div>
          </div>
        </div>
      )}
      {/* Metric grid */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 grid sm:grid-cols-2 md:grid-cols-3 gap-5">
        <InsightsStat icon={Zap}   label="Cumulative" value={fmtM3(meter.reading_m3)} testId="ins-cumulative" />
        <InsightsStat icon={Gauge} label="Remaining"  value={fmtM3(meter.remaining_flow_m3)} sub={price > 0 ? `≈ ${fmtUGX((meter.remaining_flow_m3 || 0) * price, currency)}` : null} testId="ins-remaining" />
        <InsightsStat icon={Wallet} label="Balance"    value={fmtUGX(meter.balance, currency)} testId="ins-balance" />
        <InsightsStat icon={BatteryCharging} label="Battery" value={meter.battery_voltage != null ? `${Number(meter.battery_voltage).toFixed(2)} V` : "—"} testId="ins-battery" />
        <InsightsStat icon={Signal} label="Signal"    value={`CSQ ${meter.signal_rssi ?? "—"}`} sub={meter.signal_snr != null ? `SNR ${meter.signal_snr} dB` : null} testId="ins-signal" />
        <InsightsStat icon={Power}  label="Valve"     value={<StatusPill value={meter.valve_state} />} testId="ins-valve" />
      </div>
    </div>
  );
}

function AnalyticsTab({ summary }) {
  const totals = summary?.totals || {};
  const cfg = summary?.config || {};
  const daily = summary?.daily_last_30 || [];
  const chartData = useMemo(() => daily.map((d) => ({ ...d, label: d.date.slice(5) })), [daily]);
  const avgDaily = daily.length ? (daily.reduce((s, d) => s + (d.m3 || 0), 0) / daily.length) : 0;
  const currency = cfg?.currency || "UGX";
  return (
    <div className="space-y-4" data-testid="household-analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total paid"        value={fmtUGX(totals.paid_ugx, currency)} icon={Wallet} testid="analytics-paid" />
        <MetricCard label="Gas consumed"      value={fmtM3(totals.consumption_m3)} sub={fmtKg(totals.consumption_kg)} icon={Zap} testid="analytics-consumed" />
        <MetricCard label="Cost of use"       value={fmtUGX(totals.consumption_ugx, currency)} icon={TrendingUp} testid="analytics-cost" />
        <MetricCard label="Recharges"         value={totals.recharge_count ?? 0} sub={`avg ${daily.length ? avgDaily.toFixed(3) : "0.000"} m³ / day`} icon={Repeat} testid="analytics-recharges" />
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Consumption · last 30 days</div>
        <div className="h-[220px] mt-3">
          {chartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(3)} m³`} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="m3" fill="#0f172a" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-xs text-slate-400">No consumption yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImpactTab({ summary }) {
  const carbon = summary?.carbon || {};
  const totals = summary?.totals || {};
  return (
    <div className="space-y-4" data-testid="household-impact-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5">
        <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Carbon impact</div>
        <div className="mt-2 font-heading text-3xl font-bold text-emerald-900 tabular-nums" data-testid="impact-co2">
          {Number(carbon.co2_avoided_kg || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg CO₂
        </div>
        <div className="text-xs text-emerald-800 mt-1">avoided vs firewood / charcoal cooking</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          icon={Flame}
          label="LPG consumed"
          value={fmtKg(totals.consumption_kg)}
          sub={fmtM3(totals.consumption_m3)}
          testid="impact-lpg"
        />
        <MetricCard
          icon={Wind}
          label="Firewood avoided"
          value={fmtKg(carbon.firewood_avoided_kg)}
          sub="≈ 3 kg firewood per kg LPG"
          testid="impact-firewood"
        />
        <MetricCard
          icon={TreeDeciduous}
          label="Trees saved"
          value={`≈ ${Number(carbon.trees_saved || 0).toFixed(2)}`}
          sub="≈ 20 kg firewood per small tree"
          testid="impact-trees"
        />
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-start gap-3" data-testid={testid}>
      {Icon && (
        <div className="h-8 w-8 rounded-sm bg-slate-100 grid place-items-center text-slate-500">
          <Icon size={14} strokeWidth={1.9} />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-heading text-base font-bold tabular-nums text-slate-900 leading-none mt-1">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 tabular-nums mt-1">{sub}</div>}
      </div>
    </div>
  );
}
